import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import {
  Search,
  LogOut,
  AlertTriangle,
  Heart,
  MapPin,
  Trash2,
  Phone,
  Mail,
  Car,
  ShieldCheck,
  Users,
  FileText,
  Loader2,
  Inbox,
  Gift,
} from "lucide-react";
import { API, LOGO_URL } from "../lib/constants";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("chh_admin_token") : null;
  const adminEmail =
    typeof window !== "undefined" ? localStorage.getItem("chh_admin_email") : "";

  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    georgia: 0,
    south_carolina: 0,
    flagged: 0,
    with_injuries: 0,
  });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | ga | sc | flagged | injuries
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [subs, st] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setSubmissions(subs.data);
      setStats(st.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired");
        logout();
      } else {
        toast.error("Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("chh_admin_token");
    localStorage.removeItem("chh_admin_email");
    navigate("/admin/login");
  };

  const deleteSub = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await axios.delete(`${API}/admin/submissions/${id}`, { headers });
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = useMemo(() => {
    let list = submissions;
    if (filter === "ga") list = list.filter((s) => s.state === "Georgia");
    if (filter === "sc") list = list.filter((s) => s.state === "South Carolina");
    if (filter === "flagged") list = list.filter((s) => s.flagged_out_of_service_area);
    if (filter === "injuries") list = list.filter((s) => s.had_injuries);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (s) =>
          s.full_name?.toLowerCase().includes(q) ||
          s.email?.toLowerCase().includes(q) ||
          s.phone?.toLowerCase().includes(q) ||
          s.vehicle_make?.toLowerCase().includes(q) ||
          s.vehicle_model?.toLowerCase().includes(q) ||
          s.tag_number?.toLowerCase().includes(q) ||
          s.insurance_company?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [submissions, search, filter]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard-page">
      {/* Topbar */}
      <header className="sticky top-0 z-30 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Car Hurt Help"
              className="h-9 w-9 rounded-md object-cover bg-[#0B192C]"
            />
            <div className="leading-tight">
              <div className="font-display text-base font-bold text-[#0B192C]">
                Admin Console
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] -mt-0.5">
                Car Hurt Help
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-[#4B5563]">{adminEmail}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 text-sm rounded-md px-3 py-2 border border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors text-[#0B192C]"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard label="Total leads" value={stats.total} />
          <StatCard label="Georgia" value={stats.georgia} accent="#0B192C" />
          <StatCard label="South Carolina" value={stats.south_carolina} accent="#0B192C" />
          <StatCard label="Out-of-area flagged" value={stats.flagged} accent="#D32F2F" />
          <StatCard label="With injuries" value={stats.with_injuries} accent="#D32F2F" />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-5">
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search-input"
              placeholder="Search name, email, phone, vehicle, tag, insurer..."
              className="w-full pl-10 pr-3 py-2.5 rounded-md border border-[#E2E8F0] bg-white focus:ring-2 focus:ring-[#0B192C] focus:border-[#0B192C] focus:outline-none text-sm"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="admin-filter-bar">
            {[
              ["all", "All"],
              ["ga", "GA"],
              ["sc", "SC"],
              ["flagged", "Flagged"],
              ["injuries", "Injuries"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`filter-${k}`}
                className={`text-xs uppercase tracking-[0.16em] font-semibold rounded-md px-3 py-2 transition-colors ${
                  filter === k
                    ? "bg-[#0B192C] text-white"
                    : "bg-white border border-[#E2E8F0] text-[#0B192C] hover:bg-[#F1F5F9]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-16 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-[#0B192C] mx-auto" />
              <div className="text-sm text-[#4B5563] mt-3">Loading submissions...</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center" data-testid="admin-empty-state">
              <Inbox className="h-10 w-10 text-[#9CA3AF] mx-auto" />
              <div className="font-display text-xl font-bold text-[#0B192C] mt-4">
                No submissions yet
              </div>
              <p className="text-sm text-[#4B5563] mt-1">
                Leads will appear here as they come in.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F1F5F9] sticky top-0">
                  <tr className="text-left text-[11px] uppercase tracking-[0.16em] text-[#4B5563]">
                    <th className="px-4 py-3 font-semibold">Submitted</th>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Contact</th>
                    <th className="px-4 py-3 font-semibold">State</th>
                    <th className="px-4 py-3 font-semibold">Vehicle</th>
                    <th className="px-4 py-3 font-semibold">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr
                      key={s.id}
                      onClick={() => setSelected(s)}
                      data-testid={`admin-row-${s.id}`}
                      className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC] cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-[#4B5563]">{fmt(s.created_at)}</td>
                      <td className="px-4 py-3 font-semibold text-[#0B192C]">{s.full_name}</td>
                      <td className="px-4 py-3 text-[#111827]">
                        <div>{s.phone}</div>
                        <div className="text-xs text-[#4B5563]">{s.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <StateBadge state={s.state} flagged={s.flagged_out_of_service_area} />
                      </td>
                      <td className="px-4 py-3 text-[#111827]">
                        {[s.vehicle_year, s.vehicle_make, s.vehicle_model].filter(Boolean).join(" ")}
                        <div className="text-xs text-[#4B5563]">Tag: {s.tag_number}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {s.had_injuries && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#FEF2F2] text-[#7F1D1D] px-2 py-1 rounded">
                              <Heart className="h-3 w-3" /> Injuries
                            </span>
                          )}
                          {s.had_children && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#FEF3C7] text-[#92400E] px-2 py-1 rounded">
                              <Users className="h-3 w-3" /> Children
                            </span>
                          )}
                          {s.police_report_obtained && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#ECFDF5] text-[#065F46] px-2 py-1 rounded">
                              <ShieldCheck className="h-3 w-3" /> Report
                            </span>
                          )}
                          {s.referred_by && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#EEF2FF] text-[#3730A3] px-2 py-1 rounded">
                              <Gift className="h-3 w-3" /> Referred
                            </span>
                          )}
                          {s.flagged_out_of_service_area && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#FEF2F2] text-[#7F1D1D] px-2 py-1 rounded">
                              <AlertTriangle className="h-3 w-3" /> Out of area
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Detail Sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="admin-detail-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl text-[#0B192C]">
                  {selected.full_name}
                </SheetTitle>
                <SheetDescription>
                  Submitted {fmt(selected.created_at)}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                <Section title="Contact">
                  <Detail icon={Phone} label="Phone">{selected.phone}</Detail>
                  <Detail icon={Mail} label="Email">{selected.email}</Detail>
                  <Detail icon={MapPin} label="Address">{selected.address}</Detail>
                </Section>

                <Section title="Accident">
                  <Detail label="State">
                    {selected.state === "Other"
                      ? `Other — ${selected.state_other || "—"}`
                      : selected.state}
                    {selected.flagged_out_of_service_area && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider font-bold bg-[#FEF2F2] text-[#7F1D1D] px-2 py-1 rounded">
                        Out of area
                      </span>
                    )}
                  </Detail>
                  <Detail label="Date">{selected.accident_date || "—"}</Detail>
                  <Detail label="Location">{selected.accident_location || "—"}</Detail>
                </Section>

                <Section title="Vehicle">
                  <Detail icon={Car} label="Make/Model/Year">
                    {[selected.vehicle_year, selected.vehicle_make, selected.vehicle_model]
                      .filter(Boolean)
                      .join(" ")}
                  </Detail>
                  <Detail label="Tag">{selected.tag_number}</Detail>
                  <Detail label="Insurance">{selected.insurance_company}</Detail>
                </Section>

                <Section title="Passengers & injuries">
                  <Detail icon={Users} label="Passenger count">
                    {selected.passenger_count}
                  </Detail>
                  <Detail label="Children">{selected.had_children ? "Yes" : "No"}</Detail>
                  <Detail label="Injuries">{selected.had_injuries ? "Yes" : "No"}</Detail>
                  {selected.had_injuries && (
                    <Detail label="Description">
                      {selected.injuries_description || "—"}
                    </Detail>
                  )}
                </Section>

                <Section title="Police">
                  <Detail icon={ShieldCheck} label="Police called">
                    {selected.police_called ? "Yes" : "No"}
                  </Detail>
                  <Detail label="Report obtained">
                    {selected.police_report_obtained ? "Yes" : "No"}
                  </Detail>
                  <Detail label="Case number">{selected.case_number || "—"}</Detail>
                </Section>

                {selected.referred_by && (
                  <Section title="Referral">
                    <Detail icon={Gift} label="Referred by submission">
                      <span className="font-mono text-xs break-all">{selected.referred_by}</span>
                    </Detail>
                  </Section>
                )}

                {selected.notes && (
                  <Section title="Notes">
                    <Detail icon={FileText} label="Notes">{selected.notes}</Detail>
                  </Section>
                )}

                <button
                  onClick={() => deleteSub(selected.id)}
                  data-testid="admin-delete-button"
                  className="w-full inline-flex justify-center items-center gap-2 rounded-md bg-[#FEF2F2] hover:bg-[#FEE2E2] text-[#7F1D1D] font-semibold py-3 transition-colors border border-[#FCA5A5]"
                >
                  <Trash2 className="h-4 w-4" /> Delete submission
                </button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatCard({ label, value, accent = "#0B192C" }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-sm">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#4B5563]">
        {label}
      </div>
      <div
        className="font-display text-3xl font-bold mt-2 tracking-tight"
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

function StateBadge({ state, flagged }) {
  const map = {
    Georgia: ["#ECFDF5", "#065F46"],
    "South Carolina": ["#ECFDF5", "#065F46"],
    Other: ["#FEF2F2", "#7F1D1D"],
  };
  const [bg, fg] = map[state] || ["#F1F5F9", "#0B192C"];
  return (
    <span
      className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded"
      style={{ background: flagged ? "#FEF2F2" : bg, color: flagged ? "#7F1D1D" : fg }}
    >
      {state}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#4B5563] mb-2">
        {title}
      </div>
      <div className="space-y-2 bg-[#FAFAFA] border border-[#E2E8F0] rounded-lg p-4">
        {children}
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      {Icon && <Icon className="h-4 w-4 text-[#0B192C] mt-0.5 flex-shrink-0" />}
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.16em] text-[#4B5563]">{label}</div>
        <div className="font-semibold text-[#0B192C] break-words">{children || "—"}</div>
      </div>
    </div>
  );
}
